/**
 * Create a service order on behalf of a customer (e.g. a phone-call booking).
 * A previous customer can be picked (prefilling their past patients and addresses);
 * otherwise the customer is matched by phone number and created if none exists.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  useCreateOrderMutation,
  useGetCreateOrderServicesQuery,
  useGetCreateOrderProvidersQuery,
  useLazyLookupOrderCustomerQuery,
  useSearchOrderCustomersQuery,
  useLazyGetOrderCustomerHistoryQuery,
  type CreateOrderCustomer,
  type CreateOrderPreviousAddress,
  type CreateOrderPreviousPatient,
  type CreateOrderRequest,
} from '../../store/api/ordersApi';

interface CreateOrderDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated?: (orderId: number) => void;
}

const today = () => new Date().toISOString().slice(0, 10);

const emptyForm = () => ({
  customer_phone: '',
  customer_first_name: '',
  customer_last_name: '',
  patient_is_customer: true,
  service_id: '',
  subservice_id: '',
  internal_subservice_id: '',
  provider: '', // "SPECIALIST:12" | "CONSULTANT:3" | ''
  service_price: '',
  patient_name: '',
  patient_age: '',
  patient_gender: '' as '' | 'M' | 'F',
  patient_phone: '',
  order_date: today(),
  time_from: '10:00',
  time_to: '12:00',
  street_address: '',
  building: '',
  floor: '',
  apartment: '',
  area: '',
  city: '',
  health_conditions: '',
  allergies: '',
  current_medications: '',
  special_notes: '',
  admin_notes: '',
});

type FormState = ReturnType<typeof emptyForm>;

const grid = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
  gap: 2,
  mb: 3,
};

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
    {children}
  </Typography>
);

/** First readable message from a DRF error payload. */
const firstError = (err: unknown): string | null => {
  const e = err as { data?: { message?: string; errors?: unknown } };
  const walk = (v: unknown): string | null => {
    if (typeof v === 'string') return v;
    if (Array.isArray(v)) return v.length ? walk(v[0]) : null;
    if (v && typeof v === 'object') {
      for (const [k, val] of Object.entries(v)) {
        const msg = walk(val);
        if (msg) return k === 'non_field_errors' || k === 'detail' ? msg : `${k}: ${msg}`;
      }
    }
    return null;
  };
  return walk(e?.data?.errors) ?? e?.data?.message ?? null;
};

const CreateOrderDialog = ({ open, onClose, onCreated }: CreateOrderDialogProps) => {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language?.startsWith('ar');
  const label = (o: { name: string; name_ar?: string }) => (isAr && o.name_ar ? o.name_ar : o.name);

  const [form, setForm] = useState<FormState>(emptyForm);
  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const { data: servicesData, isLoading: servicesLoading } = useGetCreateOrderServicesQuery(undefined, {
    skip: !open,
  });
  const services = useMemo(() => servicesData?.data?.services ?? [], [servicesData]);
  const service = services.find((s) => String(s.id) === form.service_id);
  const subservice = service?.subservices.find((s) => String(s.id) === form.subservice_id);
  const internalOptions = subservice?.internal_subservices ?? [];
  const internal = internalOptions.find((s) => String(s.id) === form.internal_subservice_id);
  const bookedId = internal?.id ?? subservice?.id;

  const { data: providersData, isFetching: providersLoading } = useGetCreateOrderProvidersQuery(
    bookedId as number,
    { skip: !open || !bookedId }
  );
  const providers = providersData?.data?.providers ?? [];
  const provider = providers.find((p) => `${p.provider_type}:${p.id}` === form.provider);
  const catalogPrice = provider?.price ?? internal?.price ?? subservice?.price;

  // ----- Previous customer search -----
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  useEffect(() => {
    const id = setTimeout(() => setSearchTerm(searchInput.trim()), 300);
    return () => clearTimeout(id);
  }, [searchInput]);
  const { data: searchData, isFetching: searching } = useSearchOrderCustomersQuery(searchTerm, {
    skip: !open,
  });
  const searchResults = searchData?.data?.customers ?? [];

  const [customer, setCustomer] = useState<CreateOrderCustomer | null>(null);
  const [patients, setPatients] = useState<CreateOrderPreviousPatient[]>([]);
  const [addresses, setAddresses] = useState<CreateOrderPreviousAddress[]>([]);
  const [patientPick, setPatientPick] = useState('');
  const [addressPick, setAddressPick] = useState('');
  const [loadHistory, { isFetching: historyLoading }] = useLazyGetOrderCustomerHistoryQuery();

  const applyPatient = (p: CreateOrderPreviousPatient | undefined, customerPhone: string) =>
    setForm((f) =>
      p
        ? {
            ...f,
            patient_is_customer: p.phone_number === customerPhone,
            patient_name: p.full_name,
            patient_age: String(p.age ?? ''),
            patient_gender: p.gender,
            patient_phone: p.phone_number === customerPhone ? '' : p.phone_number,
            health_conditions: p.health_conditions,
            allergies: p.allergies,
            current_medications: p.current_medications,
          }
        : {
            ...f,
            patient_is_customer: false,
            patient_name: '',
            patient_age: '',
            patient_gender: '',
            patient_phone: '',
            health_conditions: '',
            allergies: '',
            current_medications: '',
          }
    );

  const applyAddress = (a: CreateOrderPreviousAddress | undefined) =>
    setForm((f) => ({
      ...f,
      street_address: a?.street_address ?? '',
      building: a?.building ?? '',
      floor: a?.floor ?? '',
      apartment: a?.apartment ?? '',
      area: a?.area ?? '',
      city: a?.city ?? '',
    }));

  const selectCustomer = async (c: CreateOrderCustomer | null) => {
    setCustomer(c);
    setPatients([]);
    setAddresses([]);
    setPatientPick('');
    setAddressPick('');
    if (!c) {
      setForm((f) => ({ ...f, customer_phone: '', customer_first_name: '', customer_last_name: '' }));
      return;
    }
    setForm((f) => ({
      ...f,
      customer_phone: c.phone_number,
      customer_first_name: c.first_name,
      customer_last_name: c.last_name,
      patient_name: f.patient_name || [c.first_name, c.last_name].filter(Boolean).join(' '),
    }));
    const res = await loadHistory(c.id).unwrap().catch(() => null);
    if (!res) return;
    setPatients(res.data.patients);
    setAddresses(res.data.addresses);
    // Prefill with the most recent patient / address; still editable
    if (res.data.patients.length) {
      setPatientPick('0');
      applyPatient(res.data.patients[0], c.phone_number);
    }
    if (res.data.addresses.length) {
      setAddressPick('0');
      applyAddress(res.data.addresses[0]);
    }
  };

  // Typing a phone that already has an account selects that customer
  const [lookupCustomer, { data: lookupData, isFetching: lookingUp, originalArgs: lookedUpPhone }] =
    useLazyLookupOrderCustomerQuery();
  const lookupDone = !customer && lookedUpPhone === form.customer_phone.trim() && !lookingUp;
  const isNewCustomer = lookupDone && !lookupData?.data?.customer;

  const handlePhoneBlur = async () => {
    const phone = form.customer_phone.trim();
    if (customer || phone.length < 8) return;
    const res = await lookupCustomer(phone).unwrap().catch(() => null);
    const found = res?.data?.customer;
    if (found) await selectCustomer(found);
  };

  const customerName = (c: CreateOrderCustomer) =>
    [c.first_name, c.last_name].filter(Boolean).join(' ') || c.phone_number;

  const [createOrder, { isLoading: creating }] = useCreateOrderMutation();

  const handleSubmit = async () => {
    const patientName =
      form.patient_name.trim() ||
      (form.patient_is_customer
        ? [form.customer_first_name, form.customer_last_name].filter(Boolean).join(' ').trim()
        : '');
    const age = Number.parseInt(form.patient_age, 10);

    if (
      !form.customer_phone.trim() ||
      !subservice ||
      !patientName ||
      Number.isNaN(age) ||
      !form.patient_gender ||
      !form.order_date ||
      !form.street_address.trim() ||
      !form.city.trim() ||
      (!form.patient_is_customer && !form.patient_phone.trim())
    ) {
      toast.error(t('serviceOrders.create.required'));
      return;
    }
    if (form.time_from >= form.time_to) {
      toast.error(t('serviceOrders.create.invalidTime'));
      return;
    }

    const [providerType, providerId] = form.provider ? form.provider.split(':') : [null, null];
    const body: CreateOrderRequest = {
      customer_phone: form.customer_phone.trim(),
      customer_first_name: form.customer_first_name.trim(),
      customer_last_name: form.customer_last_name.trim(),
      service_id: Number(form.service_id),
      subservice_id: subservice.id,
      internal_subservice_id: internal?.id ?? null,
      provider_type: providerType as CreateOrderRequest['provider_type'],
      provider_id: providerId ? Number(providerId) : null,
      patient_name: patientName,
      patient_age: age,
      patient_gender: form.patient_gender,
      patient_phone: form.patient_is_customer ? null : form.patient_phone.trim(),
      order_date: form.order_date,
      time_from: form.time_from,
      time_to: form.time_to,
      street_address: form.street_address.trim(),
      building: form.building.trim(),
      floor: form.floor.trim(),
      apartment: form.apartment.trim(),
      area: form.area.trim(),
      city: form.city.trim(),
      health_conditions: form.health_conditions.trim(),
      allergies: form.allergies.trim(),
      current_medications: form.current_medications.trim(),
      special_notes: form.special_notes.trim(),
      admin_notes: form.admin_notes.trim(),
      service_price: form.service_price.trim() ? form.service_price.trim() : null,
    };

    try {
      const res = await createOrder(body).unwrap();
      toast.success(t('serviceOrders.create.success', { number: res.data.order_number }));
      onClose();
      onCreated?.(res.data.id);
    } catch (err) {
      toast.error(firstError(err) ?? t('serviceOrders.create.failed'));
    }
  };

  return (
    <Dialog open={open} onClose={creating ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {t('serviceOrders.create.title')}
        <Typography variant="body2" color="text.secondary">
          {t('serviceOrders.create.subtitle')}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {/* Customer */}
        <SectionTitle>{t('serviceOrders.create.customerSection')}</SectionTitle>
        {customer ? (
          <Alert
            severity={customer.is_active ? 'success' : 'error'}
            icon={historyLoading ? <CircularProgress size={20} /> : undefined}
            action={
              <Button color="inherit" size="small" onClick={() => selectCustomer(null)}>
                {t('serviceOrders.create.change')}
              </Button>
            }
            sx={{ mb: 3, alignItems: 'center' }}
          >
            <Typography fontWeight={600}>
              {customerName(customer)} · <span dir="ltr">{customer.phone_number}</span>
            </Typography>
            <Typography variant="body2">
              {customer.is_active
                ? t('serviceOrders.create.existingCustomer', { count: customer.orders_count })
                : t('serviceOrders.create.inactiveCustomer')}
            </Typography>
          </Alert>
        ) : (
          <>
            <Autocomplete
              options={searchResults}
              loading={searching}
              filterOptions={(x) => x}
              getOptionLabel={customerName}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              value={null}
              inputValue={searchInput}
              onInputChange={(_e, v, reason) => {
                if (reason !== 'reset') setSearchInput(v);
              }}
              onChange={(_e, c) => {
                setSearchInput('');
                if (c) selectCustomer(c);
              }}
              noOptionsText={t('serviceOrders.create.noCustomers')}
              renderOption={(props, c) => {
                const { key, ...rest } = props as typeof props & { key: string };
                return (
                  <li key={key} {...rest}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: 2 }}>
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          {customerName(c)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" dir="ltr">
                          {c.phone_number}
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                        {t('serviceOrders.create.ordersCount', { count: c.orders_count })}
                      </Typography>
                    </Box>
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('serviceOrders.create.searchCustomer')}
                  placeholder={t('serviceOrders.create.searchPlaceholder')}
                />
              )}
              sx={{ mb: 1 }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              {t('serviceOrders.create.orTypeNew')}
            </Typography>
            <Box sx={{ ...grid, mb: 1 }}>
              <TextField
                label={t('serviceOrders.create.customerPhone')}
                value={form.customer_phone}
                onChange={(e) => set('customer_phone', e.target.value)}
                onBlur={handlePhoneBlur}
                placeholder="01xxxxxxxxx"
                required
                fullWidth
                sx={{ gridColumn: '1 / -1' }}
                InputProps={{ endAdornment: lookingUp ? <CircularProgress size={18} /> : undefined }}
              />
              <TextField
                label={t('serviceOrders.create.firstName')}
                value={form.customer_first_name}
                onChange={(e) => set('customer_first_name', e.target.value)}
                fullWidth
              />
              <TextField
                label={t('serviceOrders.create.lastName')}
                value={form.customer_last_name}
                onChange={(e) => set('customer_last_name', e.target.value)}
                fullWidth
              />
            </Box>
            {isNewCustomer ? (
              <Alert severity="info" sx={{ mb: 3 }}>
                {t('serviceOrders.create.newCustomer')}
              </Alert>
            ) : (
              <Box sx={{ mb: 2 }} />
            )}
          </>
        )}

        {/* Service */}
        <SectionTitle>{t('serviceOrders.create.serviceSection')}</SectionTitle>
        <Box sx={grid}>
          <FormControl fullWidth required>
            <InputLabel>{t('serviceOrders.create.service')}</InputLabel>
            <Select
              label={t('serviceOrders.create.service')}
              value={form.service_id}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  service_id: e.target.value,
                  subservice_id: '',
                  internal_subservice_id: '',
                  provider: '',
                }))
              }
              disabled={servicesLoading}
            >
              {services.map((s) => (
                <MenuItem key={s.id} value={String(s.id)}>
                  {label(s)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth required disabled={!service}>
            <InputLabel>{t('serviceOrders.create.subservice')}</InputLabel>
            <Select
              label={t('serviceOrders.create.subservice')}
              value={form.subservice_id}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  subservice_id: e.target.value,
                  internal_subservice_id: '',
                  provider: '',
                }))
              }
            >
              {(service?.subservices ?? []).map((s) => (
                <MenuItem key={s.id} value={String(s.id)}>
                  {label(s)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {internalOptions.length > 0 && (
            <FormControl fullWidth>
              <InputLabel>{t('serviceOrders.create.internalSubservice')}</InputLabel>
              <Select
                label={t('serviceOrders.create.internalSubservice')}
                value={form.internal_subservice_id}
                onChange={(e) =>
                  setForm((f) => ({ ...f, internal_subservice_id: e.target.value, provider: '' }))
                }
              >
                <MenuItem value="">{t('serviceOrders.create.none')}</MenuItem>
                {internalOptions.map((s) => (
                  <MenuItem key={s.id} value={String(s.id)}>
                    {label(s)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <FormControl fullWidth disabled={!bookedId}>
            <InputLabel shrink>{t('serviceOrders.create.provider')}</InputLabel>
            <Select
              label={t('serviceOrders.create.provider')}
              value={form.provider}
              onChange={(e) => set('provider', e.target.value)}
              displayEmpty
              notched
              endAdornment={providersLoading ? <CircularProgress size={18} sx={{ mr: 3 }} /> : undefined}
            >
              <MenuItem value="">{t('serviceOrders.create.anyProvider')}</MenuItem>
              {providers.map((p) => (
                <MenuItem key={`${p.provider_type}:${p.id}`} value={`${p.provider_type}:${p.id}`}>
                  {p.full_name} ·{' '}
                  {p.provider_type === 'SPECIALIST'
                    ? t('serviceOrders.create.specialist')
                    : t('serviceOrders.create.consultant')}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label={t('serviceOrders.create.priceOverride')}
            type="number"
            inputProps={{ min: 0, step: '0.01' }}
            value={form.service_price}
            onChange={(e) => set('service_price', e.target.value)}
            helperText={
              catalogPrice !== undefined
                ? t('serviceOrders.create.catalogPrice', { price: Number(catalogPrice).toFixed(2) })
                : ' '
            }
            fullWidth
          />
        </Box>

        {/* Patient */}
        <SectionTitle>{t('serviceOrders.create.patientSection')}</SectionTitle>
        {patients.length > 0 && (
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>{t('serviceOrders.create.previousPatient')}</InputLabel>
            <Select
              label={t('serviceOrders.create.previousPatient')}
              value={patientPick}
              onChange={(e) => {
                const v = e.target.value;
                setPatientPick(v);
                applyPatient(v === 'new' ? undefined : patients[Number(v)], customer?.phone_number ?? '');
              }}
            >
              {patients.map((p, i) => (
                <MenuItem key={i} value={String(i)}>
                  {p.full_name} · {p.age} ·{' '}
                  {p.gender === 'F' ? t('serviceOrders.detail.female') : t('serviceOrders.detail.male')}
                </MenuItem>
              ))}
              <MenuItem value="new">{t('serviceOrders.create.newPatient')}</MenuItem>
            </Select>
          </FormControl>
        )}
        <FormControlLabel
          control={
            <Checkbox
              checked={form.patient_is_customer}
              onChange={(e) => set('patient_is_customer', e.target.checked)}
            />
          }
          label={t('serviceOrders.create.patientIsCustomer')}
          sx={{ mb: 1 }}
        />
        <Box sx={grid}>
          <TextField
            label={t('serviceOrders.form.name')}
            value={form.patient_name}
            onChange={(e) => set('patient_name', e.target.value)}
            placeholder={
              form.patient_is_customer
                ? [form.customer_first_name, form.customer_last_name].filter(Boolean).join(' ')
                : undefined
            }
            InputLabelProps={{ shrink: true }}
            required
            fullWidth
          />
          {!form.patient_is_customer && (
            <TextField
              label={t('serviceOrders.form.phone')}
              value={form.patient_phone}
              onChange={(e) => set('patient_phone', e.target.value)}
              required
              fullWidth
            />
          )}
          <TextField
            label={t('serviceOrders.form.age')}
            type="number"
            inputProps={{ min: 0, max: 150 }}
            value={form.patient_age}
            onChange={(e) => set('patient_age', e.target.value)}
            required
            fullWidth
          />
          <FormControl fullWidth required>
            <InputLabel>{t('serviceOrders.form.gender')}</InputLabel>
            <Select
              label={t('serviceOrders.form.gender')}
              value={form.patient_gender}
              onChange={(e) => set('patient_gender', e.target.value as 'M' | 'F')}
            >
              <MenuItem value="M">{t('serviceOrders.detail.male')}</MenuItem>
              <MenuItem value="F">{t('serviceOrders.detail.female')}</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label={t('serviceOrders.form.healthConditions')}
            value={form.health_conditions}
            onChange={(e) => set('health_conditions', e.target.value)}
            fullWidth
            sx={{ gridColumn: '1 / -1' }}
          />
          <TextField
            label={t('serviceOrders.form.allergies')}
            value={form.allergies}
            onChange={(e) => set('allergies', e.target.value)}
            fullWidth
          />
          <TextField
            label={t('serviceOrders.form.currentMedications')}
            value={form.current_medications}
            onChange={(e) => set('current_medications', e.target.value)}
            fullWidth
          />
        </Box>

        {/* Schedule */}
        <SectionTitle>{t('serviceOrders.create.scheduleSection')}</SectionTitle>
        <Box sx={{ ...grid, gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' } }}>
          <TextField
            label={t('serviceOrders.create.date')}
            type="date"
            value={form.order_date}
            onChange={(e) => set('order_date', e.target.value)}
            inputProps={{ min: today() }}
            InputLabelProps={{ shrink: true }}
            required
            fullWidth
          />
          <TextField
            label={t('serviceOrders.create.timeFrom')}
            type="time"
            value={form.time_from}
            onChange={(e) => set('time_from', e.target.value)}
            InputLabelProps={{ shrink: true }}
            required
            fullWidth
          />
          <TextField
            label={t('serviceOrders.create.timeTo')}
            type="time"
            value={form.time_to}
            onChange={(e) => set('time_to', e.target.value)}
            InputLabelProps={{ shrink: true }}
            required
            fullWidth
          />
        </Box>

        {/* Address */}
        <SectionTitle>{t('serviceOrders.create.addressSection')}</SectionTitle>
        {addresses.length > 0 && (
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>{t('serviceOrders.create.previousAddress')}</InputLabel>
            <Select
              label={t('serviceOrders.create.previousAddress')}
              value={addressPick}
              onChange={(e) => {
                const v = e.target.value;
                setAddressPick(v);
                applyAddress(v === 'new' ? undefined : addresses[Number(v)]);
              }}
            >
              {addresses.map((a, i) => (
                <MenuItem key={i} value={String(i)} sx={{ whiteSpace: 'normal' }}>
                  {[a.street_address, a.building, a.area, a.city].filter(Boolean).join(', ')}
                </MenuItem>
              ))}
              <MenuItem value="new">{t('serviceOrders.create.newAddress')}</MenuItem>
            </Select>
          </FormControl>
        )}
        <Box sx={grid}>
          <TextField
            label={t('serviceOrders.create.street')}
            value={form.street_address}
            onChange={(e) => set('street_address', e.target.value)}
            required
            fullWidth
            sx={{ gridColumn: '1 / -1' }}
          />
          <TextField
            label={t('serviceOrders.form.cityGovernorate')}
            value={form.city}
            onChange={(e) => set('city', e.target.value)}
            required
            fullWidth
          />
          <TextField
            label={t('serviceOrders.form.area')}
            value={form.area}
            onChange={(e) => set('area', e.target.value)}
            fullWidth
          />
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2, gridColumn: '1 / -1' }}>
            <TextField
              label={t('serviceOrders.create.building')}
              value={form.building}
              onChange={(e) => set('building', e.target.value)}
              fullWidth
            />
            <TextField
              label={t('serviceOrders.create.floor')}
              value={form.floor}
              onChange={(e) => set('floor', e.target.value)}
              fullWidth
            />
            <TextField
              label={t('serviceOrders.create.apartment')}
              value={form.apartment}
              onChange={(e) => set('apartment', e.target.value)}
              fullWidth
            />
          </Box>
        </Box>

        {/* Notes */}
        <SectionTitle>{t('serviceOrders.create.notesSection')}</SectionTitle>
        <Box sx={{ ...grid, mb: 0 }}>
          <TextField
            label={t('serviceOrders.form.specialNotes')}
            value={form.special_notes}
            onChange={(e) => set('special_notes', e.target.value)}
            fullWidth
            multiline
            minRows={2}
          />
          <TextField
            label={t('serviceOrders.create.adminNotes')}
            value={form.admin_notes}
            onChange={(e) => set('admin_notes', e.target.value)}
            fullWidth
            multiline
            minRows={2}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={creating}>
          {t('serviceOrders.create.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={creating || (customer ? !customer.is_active : false)}
          startIcon={creating ? <CircularProgress size={18} color="inherit" /> : undefined}
        >
          {t('serviceOrders.create.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateOrderDialog;
